/**
 * The household once the chosen aged care plan has been carried out (owner, 24 Oct 2026: carry the whole
 * aged care plan into Support at Home). Rebuilt over the calculation API with the aged care module's own
 * code (sahLinkedPosition → features/forms/planPosition.ts), so a screen waits for it (`ready`) before it
 * prices anything — never the deposit-only household first and the real one a moment later.
 */
import { useEffect, useMemo, useState } from 'react';
import { chosenAgedCarePlanOf, sahEngineContext, sahLinkedPosition, sahMoveAnswer } from './sahEngineContext.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { SavedStrategy } from '../../strategy/StrategyIntegrationUtils.ts';
import type { SahLinkedCare } from './useSahBaseline.ts';

export interface SahLinkedPositionState {
    /** The household after the aged care plan, or null (no linked plan, or it could not be rebuilt). */
    positioned: DraftFactFind | null;
    caveats: string[];
    /** Why the plan could not be rebuilt; the deposit alone is applied instead. */
    reason?: string;
    ready: boolean;
}

/** What the position depends on: the plan, and the parts of the profile a plan changes. */
const positionKey = (factFind: DraftFactFind | null | undefined, linkedRecord: SavedStrategy | null) => {
    if (!linkedRecord) return 'none';
    const f = (factFind || {}) as Record<string, unknown>;
    return JSON.stringify([linkedRecord, f.adviceFor, f.client, f.partner, f.home, f.assets, f.liabilities, f.income, f.expenses, f.structures]);
};

export function useSahLinkedPosition(factFind: DraftFactFind, linkedRecord: SavedStrategy | null): SahLinkedPositionState {
    const key = positionKey(factFind, linkedRecord);
    const [state, setState] = useState<SahLinkedPositionState & { key: string }>({ positioned: null, caveats: [], ready: !linkedRecord, key: linkedRecord ? '' : 'none' });

    useEffect(() => {
        if (!linkedRecord) { setState({ positioned: null, caveats: [], ready: true, key }); return undefined; }
        let cancelled = false;
        setState((s) => ({ ...s, ready: false }));
        const timer = setTimeout(() => {
            sahLinkedPosition(factFind, linkedRecord)
                .then((r) => { if (!cancelled) setState({ positioned: r.factFind, caveats: r.caveats, reason: r.reason, ready: true, key }); })
                .catch(() => { if (!cancelled) setState({ positioned: null, caveats: [], reason: 'The aged care plan could not be rebuilt just now, so only its deposit is applied.', ready: true, key }); });
        }, 250);
        return () => { cancelled = true; clearTimeout(timer); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    // A result for an older plan or profile is not this one's.
    return { positioned: state.positioned, caveats: state.caveats, reason: state.reason, ready: state.ready && state.key === key };
}

/**
 * The aged care plan a partner's move brings in, for a page that shows it without pricing anything
 * (the plan page): the same households step 2 reads.
 */
export function useSahLinkedCare(factFind: DraftFactFind, savedStrategies: ReadonlyArray<SavedStrategy>): SahLinkedCare | null {
    const record = useMemo(() => (sahMoveAnswer(factFind) === 'yes' ? chosenAgedCarePlanOf(savedStrategies) : null), [factFind, savedStrategies]);
    const position = useSahLinkedPosition(factFind, record);
    return useMemo(() => {
        if (!record || !position.ready) return null;
        const ctx = sahEngineContext({ factFind, savedStrategies, positioned: position.positioned });
        return { record, details: ctx.linked, before: factFind, after: ctx.baselineFactFind, whole: !!position.positioned, caveats: position.caveats, reason: position.reason };
    }, [record, position, factFind, savedStrategies]);
}
